// "How it works" — one short tour per screen, keyed by screen key (see registry.js).
//
// Each tour explains the page it is started on and nothing else: it never navigates,
// so a tour of Assessments cannot wander off into Reports. The dashboard's tour doubles
// as the first-login welcome, with a greeting put in front of it (WELCOME_STEP).
//
// Selectors are scoped to `.v2-main` (the screen) or `#v2-topbar-slot` (the buttons a
// screen puts in the top bar) because class names like .v2-views repeat from screen to
// screen. A step whose target is not on screen — an empty table, a wizard step not
// reached yet, the rail on a phone — is dropped when the tour starts, so every tour
// opens with an intro step that has no target and always shows.

const main = (sel) => `.v2-main ${sel}`;
const topbar = (sel) => `#v2-topbar-slot ${sel}`;

export const WELCOME_STEP = (firstName) => ({
  popover: {
    title: firstName ? `Welcome to Pipsqueak, ${firstName}` : "Welcome to Pipsqueak",
    description:
      "A quick look around — about a minute. Every page has its own short tour too: look for How it works in the top bar.",
  },
});

const intro = (title, description) => ({ popover: { title, description } });

// Moves the lead magnet wizard to a step (LeadMagnetScreen listens for this event).
const wizardStep = (n) => () => window.dispatchEvent(new CustomEvent("v2:wizard-step", { detail: n }));
const currentWizardStep = () =>
  Math.max(0, [...document.querySelectorAll(".v2-main .v2-wz-step")].findIndex((b) => b.classList.contains("on")));
const widgetTour = detail => () => window.dispatchEvent(new CustomEvent('v2:widget-tour', { detail }));
const restoreWidgetTour = (wizard = false) => {
  const root = document.querySelector('.v2-main .v2-lead-magnet');
  const restore = Object.fromEntries([...(root?.querySelectorAll('details[data-tour]') || [])].map(group => [group.getAttribute('data-tour'), group.open]));
  const detail = {
    ...(wizard ? { step: currentWizardStep() } : { panel: root?.querySelector('[data-active-panel]')?.getAttribute('data-active-panel') || 'Basic' }),
    restore, scroll: root?.querySelector('.v2-split-body')?.scrollTop || 0,
    mainScroll: document.querySelector('.v2-main')?.scrollTop || 0,
  };
  return widgetTour(detail);
};

export const PAGE_TOURS = {
  dashboard: [
    intro("Your dashboard", "What came in this week, what needs a hand, and the quickest way into each part of Pipsqueak."),
    {
      element: '[data-tour="quick-actions"]',
      popover: {
        title: "Your client journey",
        description:
          "These run left to right in the order you work: attract a lead, scan the prospect, map their network once they sign, then review and keep reviewing.",
        side: "bottom", align: "start",
      },
    },
    {
      element: '.v2-rail [data-menu="leadgen"]',
      popover: { title: "Lead Generation", description: "Lead magnets for your website, prospect scans before a sales call, and shareable scan links.", side: "right" },
    },
    {
      element: '.v2-rail [data-menu="discovery"]',
      popover: { title: "Discovery", description: "Every organization you work with, prospects and clients alike, with the results of their network scans.", side: "right" },
    },
    {
      element: '.v2-rail [data-menu="reviews"]',
      popover: { title: "Business Reviews", description: "Run assessments, keep a review schedule for each client, and see their latest scans.", side: "right" },
    },
    {
      element: '.v2-rail [data-menu="reports"]',
      popover: { title: "Reports", description: "Design branded report templates for what you send to prospects and clients.", side: "right" },
    },
    {
      element: ".v2-burger",
      popover: { title: "Everything else is in the menu", description: "Lead Generation, Discovery, Business Reviews and Reports all live here.", side: "bottom", align: "start" },
    },
    {
      element: '[data-tour="attention"]',
      popover: { title: "What needs a hand", description: "High-risk findings, failed scans and reviews coming due. Each row opens the screen where you deal with it.", side: "bottom" },
    },
    {
      element: '[data-tour="whats-new"]',
      popover: { title: "What's new in Pipsqueak", description: "New features land here as they ship.", side: "left" },
    },
    {
      element: '.v2-rail [data-tour="download"]',
      popover: { title: "Download the Scanner", description: "The Windows scanner that runs discovery scans on a client's network.", side: "right" },
    },
    {
      element: '[data-tour="how-it-works"]',
      popover: { title: "Help on every page", description: "Not sure what a page is for? This button walks you through whichever page you are on.", side: "bottom", align: "end" },
    },
  ],

  // The full settings panel (editing a widget, or after "Skip to all settings") and the
  // Leads view. The guided setup has its own tour, below.
  "lead-magnet": [
    intro("Lead magnets", "A free scan you embed on your own website. Visitors check their domain, and you get the lead."),
    {
      element: main(".v2-views"),
      popover: { title: "Builder and leads", description: "Build widgets here, then switch to Leads to see everyone who ran a scan through one.", side: "bottom", align: "start" },
    },
    {
      element: main(".v2-split-tabs"),
      popover: { title: "Every setting", description: "Basic, layout, form, display, widget colours and report branding — each tab is one group of settings.", side: "bottom", align: "start" },
    },
    {
      element: main(".v2-split-preview"),
      popover: { title: "Live preview", description: "Exactly what visitors will see. It updates as you change each setting.", side: "left" },
    },
    {
      element: main(".v2-builder-bar"),
      popover: { title: "Save and publish", description: "Generate the embed code for a new widget, or save changes to one you're editing — the copy already on your website picks them up.", side: "bottom", align: "end" },
    },
    {
      element: main(".v2-kpis"),
      popover: { title: "How your widgets are doing", description: "Scans, unique domains and emails captured, and the average score across them.", side: "bottom" },
    },
    {
      element: main(".v2-filters"),
      popover: { title: "Find a lead", description: "Search by domain, name, email or phone, or show only low scores — the warmest prospects.", side: "bottom", align: "start" },
    },
  ],

  'lead-magnet-studio': {
    setup: () => restoreWidgetTour(),
    steps: [
      intro('Make the widget fit your website', 'We will walk through styling, website colors, previewing, and publishing. The tour opens settings for you; it does not change your values or publish anything.'),
      {
        before: widgetTour({ panel: 'Basic' }), lazy: true, element: main('.v2-split-body'),
        popover: { title: 'Choose your offer and wording', description: 'Choose which checks to offer, then write your headline and subtitle. Your visitor enters a domain or website URL depending on the offer.', side: 'right' },
      },
      {
        before: widgetTour({ panel: 'Layout' }), lazy: true, element: main('.v2-split-body'),
        popover: { title: 'Choose how much to show', description: 'Use a full card, heading and form, or just the form when your website already provides the surrounding copy. Older widgets can keep their original appearance or switch to the new customization controls.', side: 'right' },
      },
      {
        before: widgetTour({ panel: 'Layout', group: 'widget-card-settings' }), lazy: true, element: main('[data-tour="widget-card-settings"]'),
        popover: { title: 'Background, padding and margins', description: 'Transparent backgrounds blend into your page and disable the background color control. Padding adds space inside the card. The four iframe margins add space outside it. Corners and shadows finish the card.', side: 'right' },
      },
      {
        before: widgetTour({ panel: 'Form', group: 'widget-submit-settings' }), lazy: true, element: main('[data-tour="widget-submit-settings"]'),
        popover: { title: 'Choose your submit style', description: 'Place the button inside, beside, or below the input. Enter-only mode hides the button and lets visitors press Enter to scan.', side: 'right' },
      },
      {
        before: widgetTour({ panel: 'Form', group: 'widget-input-settings' }), lazy: true, element: main('[data-tour="widget-input-settings"]'),
        popover: { title: 'Make the input readable', description: 'Set the label, placeholder, shape, text and focus colors. Turn off Transparent input to choose a fill color. Use light input text on dark websites and dark text on light websites.', side: 'right' },
      },
      {
        before: widgetTour({ panel: 'Form', group: 'widget-button-settings' }), lazy: true, element: main('[data-tour="widget-button-settings"]'),
        popover: { title: 'Style the button', description: 'Choose the wording, solid or outlined styling, colors and corners. Give the button text enough contrast. These controls are hidden when you use Enter-only submission.', side: 'right' },
      },
      {
        element: main('.v2-widget-preview-pane'),
        popover: { title: 'Check desktop, mobile and transparency', description: 'The preview updates as you edit. Switch device sizes to check the fit. The checkerboard represents transparency; use a canvas color or palette swatch to check it against your website background. Canvas colors are preview only.', side: 'left' },
      },
      {
        element: main('.v2-website-reference'),
        popover: { title: 'Match your website colors', description: 'Enter your website URL and choose Show website to detect its main colors. Remove unwanted suggestions or pick a visible color in a supported browser. These colors belong to this widget and are available beside the color controls. If a website blocks the preview, open it separately or enter a hex color.', side: 'left' },
      },
      {
        before: widgetTour({ panel: 'Display' }), lazy: true, element: main('.v2-split-body'),
        popover: { title: 'Decide where results open', description: 'Open the report in a new tab, or display it inline on your website. Inline results expand the embed, and visitors can return to the form for another scan.', side: 'right' },
      },
      {
        before: widgetTour({ panel: 'Report branding' }), lazy: true, element: main('.v2-split-body'),
        popover: { title: 'Brand the report too', description: 'The report follows your workspace branding by default. Set widget-specific overrides here when this offer needs different branding.', side: 'right' },
      },
      {
        element: main('.v2-builder-bar'),
        popover: { title: 'Save and publish', description: 'Generate embed code for a new widget, or Save changes for an existing one. Saving includes its appearance and website palette. The tour never presses these buttons for you.', side: 'bottom' },
      },
      {
        element: main('.v2-embed'),
        popover: { title: 'Test, then embed', description: 'After publishing, use Test this widget and run a real scan. Copy the iframe or GoHighLevel snippet into your website. The updated snippets resize to content and apply your margins; replace older snippets to enable those features.', side: 'top' },
      },
    ],
  },

  // The guided setup. Walks through all five steps in order, moving the wizard to each
  // one so the reader sees the real controls, then puts it back where it was.
  "lead-magnet-wizard": {
    setup: () => restoreWidgetTour(true),
    steps: [
      intro("Create a lead magnet", "Five short steps take you from an idea to a scan on your website. We'll walk through each one — nothing you've already set is lost."),
      {
        element: main(".v2-views"),
        popover: { title: "Builder and leads", description: "You're in the builder. Leads lists everyone who runs a scan through one of your widgets.", side: "bottom", align: "start" },
      },
      {
        element: main(".v2-wz-steps"),
        popover: { title: "Your progress", description: "Offer, Look, Form, Results, Publish. Click a step you've already done to go back to it.", side: "bottom", align: "start" },
      },
      {
        before: wizardStep(0), lazy: true,
        element: main(".v2-typepick"),
        popover: { title: "Step 1 · Offer", description: "Pick what the widget offers. Each lead magnet runs different checks and promises a different report — everything after this follows from it.", side: "bottom" },
      },
      {
        before: wizardStep(0), lazy: true,
        element: main(".v2-wz-chosen"),
        popover: { title: "What you picked", description: "What visitors type in, which checks run, and what the widget shows them.", side: "top" },
      },
      {
        before: wizardStep(1), lazy: true,
        element: main(".v2-wz-controls"),
        popover: { title: "Step 2 · Look", description: "Choose a layout, fonts and alignment, then write the headline and subtitle. Transparent cards blend into your website; older widgets can keep their original appearance.", side: "right" },
      },
      {
        before: widgetTour({ step: 1, group: 'widget-card-settings' }), lazy: true,
        element: main('[data-tour="widget-card-settings"]'),
        popover: { title: 'Card spacing and transparency', description: 'Turn off transparency to pick a background color. Padding is inside the card; margins add space outside the iframe. These values save with your widget.', side: 'right' },
      },
      {
        before: wizardStep(1), lazy: true,
        element: main(".v2-wz-preview"),
        popover: { title: "Live preview", description: "Check desktop and mobile sizes here. The checkerboard shows transparency. Test a solid canvas background or a saved color without changing the widget itself.", side: "left" },
      },
      {
        before: wizardStep(1), lazy: true, element: main('.v2-website-reference'),
        popover: { title: 'Bring in your website colors', description: 'Enter a website URL and choose Show website. Main colors are detected automatically; remove unwanted suggestions or pick your own. The palette is specific to this widget and is saved when you publish.', side: 'left' },
      },
      {
        before: wizardStep(2), lazy: true,
        element: main(".v2-wz-controls"),
        popover: { title: "Step 3 · Form", description: "Choose a button inside, beside or below the input, or Enter-only submission. Customize labels, placeholders, corners and colors. Turn off input transparency before selecting a background color.", side: "right" },
      },
      {
        before: wizardStep(3), lazy: true,
        element: main(".v2-wz-controls"),
        popover: { title: "Step 4 · Results", description: "Decide where the visitor's report appears: a full report in a new tab, or inside the widget on your page.", side: "right" },
      },
      {
        before: wizardStep(4), lazy: true,
        element: main(".v2-wz-controls"),
        popover: { title: "Step 5 · Publish", description: "Check the summary, then generate the embed code. Test the widget once before you paste the code into your website.", side: "right" },
      },
      {
        before: wizardStep(3), lazy: true,
        element: main(".v2-wz-actions--top"),
        popover: { title: "Back and Next", description: "Move between the steps at your own pace.", side: "bottom", align: "end" },
      },
      {
        element: main(".v2-builder-bar"),
        popover: { title: "Prefer every setting at once?", description: "Skip to all settings, and come back to the guided setup any time.", side: "bottom", align: "end" },
      },
    ],
  },

  // The assessment editor. It lives inside the Assessments screen, so it is chosen by the
  // data-tour-page attribute on its root rather than by screen key (see tourKeyFor).
  "assessment-editor": [
    intro("Inside an assessment", "Rate each item and write what you found. The score, the summary and the client report all build themselves from your ratings."),
    {
      element: ".v2-nav--slot",
      popover: { title: "Categories", description: "Work through one category at a time. The count shows how many items you've rated. SWOT, Presentation and Compare follow, and the Export downloads are at the bottom.", side: "right" },
    },
    {
      element: main(".v2-scoreband"),
      popover: { title: "The score", description: "Updates as you rate, weighted by how much each item matters. Unrated items count against it until you rate them.", side: "bottom" },
    },
    {
      element: main(".v2-views"),
      popover: { title: "Jump between sections", description: "Every category, plus SWOT and action plan, and Compare with the last review.", side: "bottom", align: "start" },
    },
    {
      element: main(".v2-ratings"),
      popover: { title: "Rate an item", description: "Red needs immediate attention, yellow needs improvement, green is in good shape. You can also leave it unrated or mark it out of scope.", side: "bottom" },
    },
    {
      element: main(".v2-linkcell"),
      popover: { title: "Write it up", description: "Click an item to open its write-up right under it: the summary, the business impact and your recommendation — what the client reads. It saves as you type, and Alt+↓ moves to the next item.", side: "right" },
    },
    {
      element: main(".v2-itemtools"),
      popover: { title: "Add or remove items", description: "Add items from the library to this category, or remove a category this client doesn't need.", side: "top", align: "start" },
    },
    {
      element: topbar(".v2-omni"),
      popover: { title: "Find an item", description: "Search across every category by name.", side: "bottom" },
    },
    {
      element: topbar('[data-tour="editor-preview"]'),
      popover: { title: "Preview the report", description: "See the client report exactly as they will, as you go.", side: "bottom" },
    },
    {
      element: topbar('[data-tour="editor-export"]'),
      popover: { title: "Export the client report", description: "Download it as a PDF to email or print, or as a PowerPoint deck you can still edit before the meeting. Works from any tab.", side: "bottom", align: "end" },
    },
    {
      element: topbar(".v2-statusselect"),
      popover: { title: "Status", description: "Draft while you work, Ready when it's done, Presented once the client has seen it — which locks it.", side: "bottom" },
    },
    {
      element: topbar('[data-tour="editor-present"]'),
      popover: { title: "Present", description: "Opens the report full screen, for the meeting.", side: "bottom", align: "end" },
    },
  ],

  widgets: [
    intro("Saved widgets", "Every lead magnet you have published, and how many leads each one has brought in."),
    {
      element: main(".v2-builder-bar"),
      popover: { title: "Spot the quiet ones", description: "Filter to widgets that have never captured a lead, or start a new one.", side: "bottom", align: "end" },
    },
    {
      element: main(".v2-tablewrap"),
      popover: { title: "Your widgets", description: "Open one to edit it. Changes reach the copy already embedded on your site — no new embed code needed.", side: "top" },
    },
  ],

  prospecting: [
    intro("Prospecting", "Scan a prospect's website before a sales call. Nothing is filed against a client, and nothing is sent until you choose."),
    {
      element: main(".v2-tabs"),
      popover: { title: "Scan, history, templates", description: "Run a new scan, find every scan you have run before, or edit the email templates you send reports with.", side: "bottom", align: "start" },
    },
    {
      element: main(".v2-scanform-row"),
      popover: { title: "Who to scan", description: "A domain or comma-separated public IPs, ranges, or CIDRs, plus an optional prospect company name. A registered company name helps report titles and automatic IP ownership matching; discovery can also use domain-based evidence without it. Prospect discovery uses this company rather than a selected customer organization. Domain targets also offer manual IP entry.", side: "bottom", align: "start" },
    },
    {
      element: main(".v2-modules"),
      popover: { title: "What to check", description: "Pick the scan modules. Everything is on by default.", side: "top" },
    },
    {
      element: main(".v2-scanform-actions"),
      popover: { title: "Run it", description: "Results come back in a few minutes. You can leave the page — the scan keeps running.", side: "top", align: "start" },
    },
    {
      element: main(".v2-reportbar"),
      popover: { title: "Share the report", description: "Download it as a PDF or send it straight to the prospect.", side: "bottom" },
    },
  ],

  scan: [
    intro("External risk scan", "Scan a client's public domain from our servers. Nothing is installed on their network."),
    {
      element: main(".v2-scanform-row"),
      popover: { title: "Domain and organization", description: "The result is filed against the organization you pick. Automatic IP discovery also uses its name, website and managed inventory to confirm ownership, so choose the correct organization with its registered company name.", side: "bottom", align: "start" },
    },
    {
      element: main(".v2-modules"),
      popover: { title: "What to check", description: "Pick the scan modules. All four are on by default.", side: "top" },
    },
    {
      element: main(".v2-scanform-actions"),
      popover: { title: "Run it", description: "Usually takes a few minutes. Results arrive all at once.", side: "top", align: "start" },
    },
  ],

  "scan-links": [
    intro("Scan links", "A link you send to someone so they can run the scan themselves. Each one lasts seven days."),
    {
      element: main(".v2-pane-side"),
      popover: { title: "Make a link", description: "Enter a domain or comma-separated public IP addresses, ranges or CIDRs (up to 256 unique IPs), then choose its organization. Automatic IP discovery uses the saved organization name, website and managed inventory. Add who to email it to and choose the email template.", side: "left" },
    },
    {
      element: main(".v2-views"),
      popover: { title: "Track your links", description: "See which links are still active, which have expired, and any you have revoked.", side: "bottom", align: "start" },
    },
  ],

  organizations: [
    intro("Organizations", "Every prospect and client in your workspace, with their technology health and network scan results."),
    {
      element: main(".v2-pagehead-actions"),
      popover: { title: "Add an organization", description: "Create one here before you upload its first network scan.", side: "bottom", align: "end" },
    },
    {
      element: main(".v2-metrics"),
      popover: { title: "At a glance", description: "Active clients, open prospects, managed revenue and who needs attention.", side: "bottom" },
    },
    {
      element: main(".v2-views"),
      popover: { title: "Clients or prospects", description: "Switch between everyone, just clients or just prospects.", side: "bottom", align: "start" },
    },
    {
      element: main(".v2-tablewrap"),
      popover: { title: "Open an organization", description: "Click any row to expand its network scan results right here.", side: "top" },
    },
  ],

  "review-overview": [
    intro("Business reviews", "Where your assessments and client reviews come together."),
    {
      element: topbar(".v2-btn--primary"),
      popover: { title: "Start an assessment", description: "A baseline, a proposal or a regular review — pick a client and a template.", side: "bottom", align: "end" },
    },
    {
      element: main(".v2-metrics"),
      popover: { title: "Where things stand", description: "Assessments in progress, clients you manage, and reviews that are due.", side: "bottom" },
    },
    {
      element: main(".v2-reviewpanes"),
      popover: { title: "Pick up where you left off", description: "Your most recent work, and the business reviews coming up.", side: "top" },
    },
  ],

  assessments: [
    intro("Assessments", "Every assessment you have run, from first draft to presented."),
    {
      element: topbar(".v2-btn--primary"),
      popover: { title: "New assessment", description: "Pick a client and a template to start one.", side: "bottom", align: "end" },
    },
    {
      element: main(".v2-kpis"),
      popover: { title: "Your pipeline", description: "How many are drafts, how many are ready to present, and how many clients they cover.", side: "bottom" },
    },
    {
      element: main(".v2-views"),
      popover: { title: "Filter by status", description: "Draft, ready or presented.", side: "bottom", align: "start" },
    },
    {
      element: main(".v2-tablewrap"),
      popover: { title: "Open one", description: "Click an assessment to rate its items, add notes and present it.", side: "top" },
    },
  ],

  "review-schedule": [
    intro("Review schedule", "Who is due for a business review, overdue first."),
    {
      element: main(".v2-views"),
      popover: { title: "What's coming", description: "The next 90 days, anything overdue, and clients with no schedule yet.", side: "bottom", align: "start" },
    },
    {
      element: main(".v2-cadence"),
      popover: { title: "Set the cadence", description: "Choose how often each client is reviewed. The due date follows from it.", side: "bottom" },
    },
    {
      element: main(".v2-tablewrap"),
      popover: { title: "Reviews and scans side by side", description: "The last review and the last scan sit next to each other, so both stay on track.", side: "top" },
    },
  ],

  "review-templates": [
    intro("Assessment templates", "What each assessment asks. These are the questions — report templates handle the layout."),
    {
      element: main(".v2-views"),
      popover: { title: "Templates and items", description: "A template is a set of items. Build items once in the library and reuse them across templates.", side: "bottom", align: "start" },
    },
    {
      element: topbar(".v2-btn--primary"),
      popover: { title: "Add your own", description: "Create a new template, or a new item for the library.", side: "bottom", align: "end" },
    },
  ],

  "reviews-scans": [
    intro("Latest scans", "The most recent external scan for each organization, next to the review that goes with it."),
    {
      element: main(".v2-tablewrap"),
      popover: { title: "Scan and review together", description: "Sort by score to find who needs a conversation first.", side: "top" },
    },
  ],

  templates: [
    intro("Report templates", "Design the branded reports you send to prospects and clients."),
    {
      element: main(".v2-tplbar"),
      popover: { title: "Find or create", description: "Search your templates, or start a new one in the Report Builder.", side: "bottom" },
    },
    {
      element: main(".v2-views"),
      popover: { title: "Yours and the defaults", description: "Your own templates, and the ready-made defaults. Clone a default to make it your own.", side: "bottom", align: "start" },
    },
    {
      element: main(".v2-tpl-grid"),
      popover: { title: "Open a template", description: "Edit it, clone it, or make a report from it with a scan.", side: "top" },
    },
  ],

  downloads: [
    intro("Download the Scanner", "The Windows scanner that maps a client's internal network."),
    {
      element: main(".v2-release"),
      popover: { title: "Get the latest version", description: "Run it on a machine inside the client's network, then upload the results to their organization here.", side: "bottom" },
    },
  ],
};

export const hasPageTour = (screen) => Boolean(PAGE_TOURS[screen]);

// Which tour How it works should run. Usually the screen's own, but a view rendered inside
// a screen — the assessment editor inside Assessments, the lead magnet wizard inside Lead
// magnets — names its own with a data-tour-page attribute, and that wins.
export const tourKeyFor = (screen) => {
  const own = document.querySelector(".v2-main [data-tour-page]")?.getAttribute("data-tour-page");
  return own && PAGE_TOURS[own] ? own : screen;
};
